import type {ReactNode} from "react";

import {Button, Typography} from "@project/design-system-web";
import {useI18n} from "@project/i18n/react";

import {componentMessages} from "../../components.messages";

import {AppAlertDialog} from "./app-alert-dialog";

interface ConfirmSoftDeleteDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onConfirm: () => void;
  isLoading?: boolean;
  title?: string;
  description?: ReactNode;
  itemName?: string;
  confirmLabel?: string;
}

export function ConfirmSoftDeleteDialog({
  isOpen,
  onOpenChange,
  onConfirm,
  isLoading = false,
  title,
  description,
  itemName,
  confirmLabel,
}: ConfirmSoftDeleteDialogProps) {
  const {t} = useI18n();
  const resolvedTitle = title ?? t(componentMessages.confirmSoftDeleteTitle);
  const resolvedConfirmLabel = confirmLabel ?? t(componentMessages.confirmDeleteConfirm);
  let resolvedDescription: ReactNode;

  if (description) {
    resolvedDescription =
      typeof description === "string" ? <Typography>{description}</Typography> : description;
  } else if (itemName) {
    resolvedDescription = (
      <Typography>
        {t(componentMessages.confirmSoftDeleteDescriptionWithItemPrefix)}{" "}
        <strong>{itemName}</strong>.{" "}
        {t(componentMessages.confirmSoftDeleteDescriptionWithItemSuffix)}
      </Typography>
    );
  } else {
    resolvedDescription = (
      <Typography>{t(componentMessages.confirmSoftDeleteDescriptionGeneric)}</Typography>
    );
  }

  return (
    <AppAlertDialog.Root isOpen={isOpen} onOpenChange={onOpenChange}>
      <AppAlertDialog.Backdrop>
        <AppAlertDialog.Container>
          <AppAlertDialog.Dialog className="sm:max-w-100">
            <AppAlertDialog.CloseTrigger />
            <AppAlertDialog.Header>
              <AppAlertDialog.Icon status="danger" />
              <AppAlertDialog.Heading>{resolvedTitle}</AppAlertDialog.Heading>
            </AppAlertDialog.Header>
            <AppAlertDialog.Body>{resolvedDescription}</AppAlertDialog.Body>
            <AppAlertDialog.Footer>
              <Button slot="close" variant="tertiary">
                {t(componentMessages.confirmDeleteCancel)}
              </Button>
              <Button isDisabled={isLoading} onPress={onConfirm} variant="danger">
                {resolvedConfirmLabel}
              </Button>
            </AppAlertDialog.Footer>
          </AppAlertDialog.Dialog>
        </AppAlertDialog.Container>
      </AppAlertDialog.Backdrop>
    </AppAlertDialog.Root>
  );
}
